import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

export default function SprintIntake() {
  const { user, token, isImpersonating } = useAuth(); 
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Modal State Management
  const [modalState, setModalState] = useState({ 
    isOpen: false, 
    type: '', 
    title: '', 
    message: '' 
  });

  // NEW: Load both Questions AND Existing Answers on mount
  useEffect(() => {
    const loadAssessmentData = async () => {
      setLoading(true);
      await fetchQuestions();
      await fetchExistingAnswers();
      setLoading(false);
    };
    
    loadAssessmentData();
  }, [token, user?.org_id]); // Added dependencies to ensure it runs when context is ready

  const fetchQuestions = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await axios.get(`${baseURL}/api/sprint/questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(res.data || []);
    } catch (err) {
      console.error("Failed to load questions:", err);
      setQuestions([]); 
    }
  };

  // NEW: Function to pull saved answers from the database
  const fetchExistingAnswers = async () => {
    if (!user?.org_id) return;
    
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await axios.get(`${baseURL}/api/sprint/answers/${user.org_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert database array [{question_id, answer_data}] into state object { question_id: answer_data }
      if (res.data && res.data.length > 0) {
        const loadedAnswers = {};
        res.data.forEach(row => {
          loadedAnswers[row.question_id] = row.answer_data;
        });
        setAnswers(loadedAnswers);
      }
    } catch (err) {
      console.error("Failed to load existing answers:", err);
    }
  };

  const handleChange = (questionId, value, type) => {
    if (isImpersonating) return;

    if (type === 'MULTI_SELECT') {
      const currentSelections = answers[questionId] || [];
      const newSelections = currentSelections.includes(value)
        ? currentSelections.filter(item => item !== value)
        : [...currentSelections, value];
      setAnswers({ ...answers, [questionId]: newSelections });
    } else {
      setAnswers({ ...answers, [questionId]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isImpersonating) return; 

    setSubmitting(true);
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await axios.post(`${baseURL}/api/sprint/submit`, {
        orgId: user.org_id,
        answers: answers 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setModalState({
        isOpen: true,
        type: 'success',
        title: 'Assessment Completed',
        message: 'Your baseline data has been submitted. Our systems are analyzing your inputs to generate your preliminary Readiness Score.'
      });
      
    } catch (err) {
      console.error(err);
      setModalState({
        isOpen: true,
        type: 'error',
        title: 'Submission Failed',
        message: 'There was an issue saving your answers. Please check your connection and try again, or contact support if the issue persists.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => setModalState({ ...modalState, isOpen: false });

  if (loading) return <div className="p-8 text-center text-slate-500">Loading Readiness Assessment...</div>;

  const sortedQuestions = Array.isArray(questions) 
    ? [...questions].sort((a, b) => a.order_index - b.order_index) 
    : [];

  const groupedQuestions = sortedQuestions.reduce((acc, q) => {
    const section = q.section_name || 'General';
    (acc[section] = acc[section] || []).push(q);
    return acc;
  }, {});

  return (
    <div className="p-8 max-w-4xl mx-auto pb-24 relative">
      
      {modalState.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center transform transition-all">
            {modalState.type === 'success' ? (
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">✓</div>
            ) : (
              <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">!</div>
            )}
            
            <h3 className="text-2xl font-bold text-slate-900 mb-2">{modalState.title}</h3>
            <p className="text-slate-600 mb-8">{modalState.message}</p>
            
            <button
              onClick={closeModal}
              className={`w-full font-semibold py-3 px-6 rounded-xl transition-colors ${
                modalState.type === 'success' 
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                  : 'bg-slate-900 hover:bg-slate-800 text-white'
              }`}
            >
              {modalState.type === 'success' ? 'Return to Dashboard' : 'Close and Try Again'}
            </button>
          </div>
        </div>
      )}

      <div className="mb-8 border-b border-slate-200 pb-6">
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Pre-Sprint Intake</h1>
        <p className="text-slate-500 mt-2">Please complete this readiness assessment to baseline your organization's AI posture before the 5-Day Sprint begins.</p>
      </div>

      {isImpersonating && (
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg flex items-start gap-3 shadow-sm">
          <span className="text-xl">👁️</span>
          <div>
            <h3 className="font-bold text-sm">Preview Mode Active</h3>
            <p className="text-xs mt-1">You are currently impersonating a client. This form is strictly read-only so you can preview their assigned questions and submitted answers.</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-10">
        {Object.entries(groupedQuestions).map(([sectionName, sectionQuestions], sectionIndex) => (
          <div key={sectionName} className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
            
            <h2 className="text-xl font-semibold text-blue-900 mb-6 border-b border-slate-100 pb-2">
              Part {sectionIndex + 1}: {sectionName}
            </h2>
            
            <div className="space-y-8">
              {sectionQuestions.map((q) => (
                <div key={q.id}>
                  
                  <label className="block text-sm font-semibold text-slate-800 mb-3">
                    {q.order_index}. {q.question_text}
                  </label>

                  {/* TEXT INPUT */}
                  {q.question_type === 'TEXT' && (
                    <textarea 
                      required={!isImpersonating}
                      disabled={isImpersonating}
                      value={answers[q.id] || ''} 
                      className={`w-full border rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 ${isImpersonating ? 'bg-slate-100 border-slate-200 cursor-not-allowed text-slate-600' : 'border-slate-300'}`}
                      rows="3"
                      placeholder={isImpersonating ? "No answer provided" : "Type your answer here..."}
                      onChange={(e) => handleChange(q.id, e.target.value, 'TEXT')}
                    />
                  )}

                  {/* SINGLE SELECT */}
                  {q.question_type === 'SINGLE_SELECT' && (
                    <div className="space-y-2">
                      {Array.isArray(q.options) && q.options.length > 0 ? (
                        q.options.map((opt, idx) => (
                          <label key={idx} className={`flex items-center gap-3 p-3 border border-slate-200 rounded-lg transition ${isImpersonating ? 'bg-slate-50 opacity-80 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
                            <input 
                              type="radio" 
                              name={`question_${q.id}`} 
                              value={opt}
                              checked={answers[q.id] === opt} 
                              required={!isImpersonating}
                              disabled={isImpersonating}
                              onChange={(e) => handleChange(q.id, e.target.value, 'SINGLE_SELECT')}
                              className={`w-4 h-4 text-blue-600 focus:ring-blue-500 ${isImpersonating ? 'cursor-not-allowed' : ''}`}
                            />
                            <span className="text-sm text-slate-700">{opt}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-xs text-red-500 italic">Error: Missing options in database for this question.</p>
                      )}
                    </div>
                  )}

                  {/* MULTI SELECT */}
                  {q.question_type === 'MULTI_SELECT' && (
                    <div className="space-y-2">
                      {Array.isArray(q.options) && q.options.length > 0 ? (
                        q.options.map((opt, idx) => (
                          <label key={idx} className={`flex items-center gap-3 p-3 border border-slate-200 rounded-lg transition ${isImpersonating ? 'bg-slate-50 opacity-80 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'}`}>
                            <input 
                              type="checkbox" 
                              value={opt}
                              checked={(answers[q.id] || []).includes(opt)} 
                              disabled={isImpersonating}
                              onChange={(e) => handleChange(q.id, e.target.value, 'MULTI_SELECT')}
                              className={`w-4 h-4 text-blue-600 focus:ring-blue-500 rounded border-slate-300 ${isImpersonating ? 'cursor-not-allowed' : ''}`}
                            />
                            <span className="text-sm text-slate-700">{opt}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-xs text-red-500 italic">Error: Missing options in database for this question.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="sticky bottom-6 z-40 bg-slate-900 p-4 rounded-xl flex items-center justify-between shadow-2xl">
          <p className="text-slate-300 text-sm">
            {isImpersonating ? 'This form is locked in Preview Mode.' : 'Please ensure all required fields are complete before submitting.'}
          </p>
          
          <button 
            type="submit" 
            disabled={submitting || isImpersonating}
            className={`font-semibold py-2 px-8 rounded-lg transition-colors ${
              isImpersonating 
                ? 'bg-slate-700 text-slate-500 cursor-not-allowed border border-slate-600' 
                : 'bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50'
            }`}
          >
            {isImpersonating ? '🔒 Read-Only Mode' : submitting ? 'Analyzing Data...' : 'Submit Baseline Data'}
          </button>
        </div>
      </form>
    </div>
  );
}